import React, { useEffect, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import {
  useLocation,
  useNavigate,
  Link,
  useSearchParams,
} from "react-router-dom";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  Chip,
  TextField,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Popover,
} from "@mui/material";
import { FaFilter } from "react-icons/fa";
import { IoIosArrowBack, IoMdClose } from "react-icons/io";

const stringToColor = (str) => {
  if (!str) return "#e0e0e0";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash) % 360;
  return `hsl(${h}, 70%, 80%)`;
};

const statusOptions = ["ENABLED", "PAUSED", "REMOVED"];

const removeDuplicates = (data) => {
  const uniqueData = new Map();
  data.forEach((item) => {
    if (!uniqueData.has(item.id)) {
      uniqueData.set(item.id, item);
    }
  });
  return Array.from(uniqueData.values());
};

const FilterInputView = ({ column, onApply, onBack, onClose }) => {
  const [stringValue, setStringValue] = useState("");
  const [stringCondition, setStringCondition] = useState("contains");
  const [selectedStatus, setSelectedStatus] = useState([]);

  const handleApply = () => {
    let filterObj = null;
    switch (column.type) {
      case "name":
        if (!stringValue) return alert("Please enter a value.");
        filterObj = {
          title: column.title,
          key: "name",
          condition: stringCondition,
          type: "string",
          value: stringValue,
        };
        break;
      case "status":
        if (selectedStatus.length === 0)
          return alert("Please select at least one status.");
        filterObj = {
          title: column.title,
          key: "status",
          condition: "is one of",
          type: "multi-select",
          value: selectedStatus,
        };
        break;
      default:
        return;
    }
    onApply(filterObj);
  };

  const renderFilterInput = () => {
    switch (column.type) {
      case "name":
        return (
          <div className="flex items-center gap-2">
            <Select
              value={stringCondition}
              onChange={(e) => setStringCondition(e.target.value)}
              size="small"
              className="flex-shrink-0"
            >
              <MenuItem value="contains">contains</MenuItem>
              <MenuItem value="not contains">does not contain</MenuItem>
            </Select>
            <TextField
              type="text"
              value={stringValue}
              onChange={(e) => setStringValue(e.target.value)}
              placeholder="Enter text..."
              size="small"
              fullWidth
            />
          </div>
        );
      case "status":
        return (
          <FormControl fullWidth size="small">
            <InputLabel>Select Status</InputLabel>
            <Select
              multiple
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              input={<OutlinedInput label="Select Status" />}
              renderValue={(selected) => (
                <div className="flex gap-1 flex-wrap">
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </div>
              )}
            >
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      default:
        return <p>Unsupported filter type.</p>;
    }
  };

  return (
    <div className="w-80">
      <div className="flex items-center justify-between p-2 border-b">
        <IconButton onClick={onBack} size="small">
          <IoIosArrowBack />
        </IconButton>
        <h3 className="font-semibold">{column.title}</h3>
        <IconButton onClick={onClose} size="small">
          <IoMdClose />
        </IconButton>
      </div>
      <div className="p-4 space-y-4">{renderFilterInput()}</div>
      <div className="flex justify-end gap-2 p-2 bg-gray-50 border-t">
        <Button onClick={handleApply} variant="contained" size="small">
          Apply Filter
        </Button>
      </div>
    </div>
  );
};

const CampaignTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const engine = searchParams.get("engine");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);

  const [activeFilters, setActiveFilters] = useState([]);

  const [columns] = useState([
    { id: 1, title: "ID", key: "id", type: "string", visible: true },
    { id: 2, title: "Name", key: "name", type: "string", visible: true },
    { id: 3, title: "Group", key: "groups", type: "string", visible: true },
  ]);

  useEffect(() => {
    const groupType = location.state?.groupType || "campaign";
    setLoading(true);

    if (groupType === "campaign") {
      fetchCampaigns();
    } else {
      fetchKeywords();
    }
  }, [location.state?.groupType]);

  // Hook to handle auto-generation of the group name based on groupType and current date
  useEffect(() => {
    if (selectedRows.length > 0) {
      setGroupName((prev) => {
        if (!prev) {
          const groupType = location.state?.groupType || "campaign";
          const formattedType = groupType === "campaign" ? "Campaign" : "Keyword";
          
          const today = new Date();
          const day = String(today.getDate()).padStart(2, "0");
          const month = String(today.getMonth() + 1).padStart(2, "0");
          const year = today.getFullYear();
          const formattedDate = `${day}/${month}/${year}`;
          
          return `${formattedType} Group ${formattedDate}`;
        }
        return prev;
      });
    } else {
      setGroupName("");
    }
  }, [selectedRows.length, location.state?.groupType]);

  const fetchCampaigns = () => {
    fetch("https://api.confidanto.com/campaigns/get", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        project_id: localStorage.getItem("project_id"),
        customer_id: localStorage.getItem("customer_id"),
        email: localStorage.getItem("email"),
        platform: localStorage.getItem("platform"),
        ad_account_id: localStorage.getItem("ad_account_id"),
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        let structData = data?.map((item) => ({
          ...item,
          id: item.campaign_id,
          name: item.campaign_name,
        }));
        structData = removeDuplicates(structData);
        setCampaigns(structData || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching campaigns:", error);
        setLoading(false);
      });
  };

  const fetchKeywords = () => {
    fetch("https://api.confidanto.com/keywords/get", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        project_id: localStorage.getItem("project_id"),
        customer_id: localStorage.getItem("customer_id"),
        email: localStorage.getItem("email"),
        platform: localStorage.getItem("platform"),
        ad_account_id: localStorage.getItem("ad_account_id"),
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        let structData = data?.map((item) => ({
          ...item,
          id: item.id,
          name: item.name,
        }));
        structData = removeDuplicates(structData);
        setCampaigns(structData || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching keywords:", error);
        setLoading(false);
      });
  };

  const handleSelectOne = (id, key) => {
    const selectedItem = campaigns.find((c) => c[key] === id);
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );

    setSelectedRows((prev) => {
      const exists = prev.find((row) => row[key] === id);
      if (exists) {
        return prev.filter((row) => row[key] !== id);
      } else {
        return [...prev, selectedItem];
      }
    });
  };

  // --- Filter Management ---
  const handleFilterButtonClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setSelectedColumn(null);
  };

  const handleColumnSelect = (column) => {
    if (
      column.type === "status" &&
      activeFilters.some((f) => f.type === "multi-select")
    ) {
      return;
    }
    setSelectedColumn(column);
  };

  const handleApplyFilter = (filterObj) => {
    setActiveFilters((prev) => [...prev, { ...filterObj, id: Date.now() }]);
    handlePopoverClose();
  };

  const removeFilter = (id) => {
    setActiveFilters((prev) => prev.filter((f) => f.id !== id));
  };

  // --- Filtering Logic ---
  const filteredCampaigns = campaigns.filter((campaign) => {
    if (activeFilters.length === 0) return true;

    return activeFilters.every((filter) => {
      const cellValue = campaign[filter.key];
      if (filter.type === "string") {
        const valueLower = String(cellValue || "").toLowerCase();
        const filterValueLower = filter.value.toLowerCase();
        if (filter.condition === "contains") {
          return valueLower.includes(filterValueLower);
        }
        if (filter.condition === "not contains") {
          return !valueLower.includes(filterValueLower);
        }
      }
      if (filter.type === "multi-select") {
        const statusValue = String(cellValue || "").toUpperCase();
        return filter.value.includes(statusValue);
      }
      return true;
    });
  });

  const handleSelectAll = (event) => {
    const itemsToSelect = filteredCampaigns;
    if (event.target.checked) {
      setSelected(itemsToSelect.map((c) => c.id));
      setSelectedRows(itemsToSelect);
    } else {
      setSelected([]);
      setSelectedRows([]);
    }
  };

  const isAllSelected =
    filteredCampaigns.length > 0 &&
    selected.length === filteredCampaigns.length;

  const handleGroupSave = async () => {
    const groupType = location.state?.groupType || "campaign";
    let groupData = {
      customer_id: localStorage.getItem("customer_id"),
      email: localStorage.getItem("email"),
      group_name: groupName,
      number_of_campaigns: selectedRows.length,
      ...(groupType === "campaign"
        ? {
            create_by_campaign: true,
            campaign_ids: selectedRows.map((row) => row.id),
            campaign_names: selectedRows.map((row) => row.name),
          }
        : {
            create_by_keywords: true,
            keyword_ids: selectedRows.map((row) => row.id),
            keyword_names: selectedRows.map((row) => row.name),
          }),
      ad_account_id: localStorage.getItem("ad_account_id"),
    };
    try {
      const response = await fetch(
        "https://api.confidanto.com/campaign-group/create",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(groupData),
        },
      );
      const result = await response.json();
      if (!response.ok)
        throw new Error(result.error || "Failed to create group.");
      setSnackbarOpen(true);
      setTimeout(() => navigate("/google-ads/group"), 1200);
    } catch (error) {
      setSnackbarError(
        error.message.includes("already exists")
          ? "Group name already exists. Please choose a different name."
          : error.message || "Failed to create group. Try again later.",
      );
    }
  };

  const open = Boolean(anchorEl);

  const renderTableCell = (row, col) => {
    switch (col.key) {
      case "id":
        return (
          <Checkbox
            checked={selected.includes(row[col.key])}
            onChange={() => handleSelectOne(row[col.key], col.key)}
          />
        );
      case "groups":
        if (Array.isArray(row[col.key])) {
          return (
            <div className="flex flex-wrap gap-2">
              {row.groups.length > 0 ? (
                row.groups.map((group, idx) => (
                  <div
                    key={idx}
                    className="flex items-center bg-gray-50 border border-gray-200 rounded-md overflow-hidden"
                  >
                    {/* Color Strip */}
                    <div
                      className="w-3 h-8"
                      style={{
                        backgroundColor: stringToColor(group.group_name),
                      }}
                    ></div>
                    {/* Text */}
                    <span className="px-2 text-sm text-gray-700 font-medium">
                      {group.group_name}
                    </span>
                  </div>
                ))
              ) : (
                <span className="text-gray-400 text-sm">No Group</span>
              )}
            </div>
          );
        } else {
          return row[col.key];
        }
      default:
        return row[col.key];
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="p-4">
      <div className="mb-4 space-y-3">
        <div className="flex justify-start items-center">
          <Link to="/google-ads/group">
            <IoIosArrowBack className="h-8 w-8 shadow-lg hover:bg-slate-200 rounded-lg mt-1 mr-2" />
          </Link>
          <div className="flex-row">
            <button
              onClick={handleFilterButtonClick}
              className="hover:bg-slate-200 p-2 rounded text-gray-600 relative"
            >
              <FaFilter className="inline mr-1" /> Add filter
              {activeFilters.length > 0 && (
                <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
                  {activeFilters.length}
                </span>
              )}
            </button>

            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              {selectedColumn ? (
                <FilterInputView
                  column={selectedColumn}
                  onApply={handleApplyFilter}
                  onBack={() => setSelectedColumn(null)}
                  onClose={handlePopoverClose}
                />
              ) : (
                <div className="w-60">
                  <h3 className="p-3 font-semibold border-b">Filter by</h3>
                  <MenuItem
                    onClick={() =>
                      handleColumnSelect({
                        type: "name",
                        title: `${
                          location.state?.groupType === "campaign"
                            ? "Campaign"
                            : "Keyword"
                        } Name`,
                      })
                    }
                  >
                    {location.state?.groupType === "campaign"
                      ? "Campaign"
                      : "Keyword"}{" "}
                    Name
                  </MenuItem>
                  <MenuItem
                    onClick={() =>
                      handleColumnSelect({ type: "status", title: "Status" })
                    }
                    disabled={activeFilters.some(
                      (f) => f.type === "multi-select",
                    )}
                  >
                    Status
                  </MenuItem>
                </div>
              )}
            </Popover>
          </div>
        </div>

        {activeFilters.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap p-2 bg-gray-50 rounded-md border">
            {activeFilters.map((filter) => (
              <Chip
                key={filter.id}
                label={`${filter.title} ${filter.condition} '${
                  Array.isArray(filter.value)
                    ? filter.value.join(", ")
                    : filter.value
                }'`}
                onDelete={() => removeFilter(filter.id)}
              />
            ))}
            <Button
              variant="text"
              size="small"
              onClick={() => setActiveFilters([])}
              sx={{ marginLeft: "auto" }}
            >
              Clear All
            </Button>
          </div>
        )}
      </div>

      {selectedRows.length > 0 && (
        <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
          <TextField
            label="Name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            sx={{ width: 300 }}
          />
          <Button
            variant="contained"
            onClick={handleGroupSave}
            disabled={!groupName}
          >
            Create Group
          </Button>
        </div>
      )}

      <table className="min-w-full border border-gray-300">
        <thead className="bg-[#2930a8] text-white">
          <tr>
            {columns
              .filter((col) => col.visible)
              .map((col) => (
                <th key={col.id} className="border px-2 py-2 text-left">
                  {col.key === "id" ? (
                    <div className="flex justify-center">
                      <Checkbox
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        color="default"
                        className="text-white"
                        indeterminate={
                          selected.length > 0 &&
                          selected.length < filteredCampaigns.length
                        }
                      />
                    </div>
                  ) : (
                    <>{col.title}</>
                  )}
                </th>
              ))}
          </tr>
        </thead>
        <tbody>
          {filteredCampaigns.map((campaign, index) => (
            <tr
              key={campaign.id || index}
              className="bg-white hover:bg-gray-50"
            >
              {columns
                .filter((col) => col.visible)
                .map((col) => (
                  <td
                    key={col.id}
                    className={`border px-2 py-2 ${
                      col.key === "id" ? "text-center" : "text-left"
                    }`}
                  >
                    {renderTableCell(campaign, col)}
                  </td>
                ))}
            </tr>
          ))}
        </tbody>
      </table>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          Group created successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!snackbarError}
        autoHideDuration={4000}
        onClose={() => setSnackbarError("")}
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          {snackbarError}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CampaignTable;